/**
 * Resolver uma ocorrência de custódia (E136.F3, GUS-152, ADR-0092 §2.2): quem atende o caso escolhe
 * como ela foi resolvida e, se quiser, deixa uma observação.
 *
 * - Restaurado: o servidor confere o arquivo de novo e só registra se ele confere; senão a
 *   conferência fica registrada e a ocorrência segue aberta (a tela diz isso).
 * - Retirado do exame / reenviado pelo banco: a decisão fica registrada e o arquivo segue fora do
 *   exame.
 *
 * O banco recebe a recusa neutra: a resolução é de quem atende o caso.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Button, Field, Radio, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  ACOES_DA_RESOLUCAO,
  type AcaoDaResolucao,
  ResolucaoDaOcorrenciaError,
  resolverOcorrencia,
} from '@/lib/custodia';

const t = ptBR.abertura.custodia.ocorrencias;
const NOTA_MAX = 1000;

export interface ResolverOcorrenciaProps {
  client: AxiosInstance;
  caseId: string;
  ocorrenciaId: string;
  onResolvido: () => Promise<void>;
  onCancelar: () => void;
}

export function ResolverOcorrencia({
  client,
  caseId,
  ocorrenciaId,
  onResolvido,
  onCancelar,
}: ResolverOcorrenciaProps): JSX.Element {
  const [acao, setAcao] = useState<AcaoDaResolucao | null>(null);
  const [nota, setNota] = useState('');
  const [registrando, setRegistrando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function registrar(): Promise<void> {
    if (acao === null) return;
    setRegistrando(true);
    setErro(null);
    try {
      await resolverOcorrencia(client, caseId, ocorrenciaId, {
        acao,
        nota: nota.trim() === '' ? null : nota.trim(),
      });
      await onResolvido();
    } catch (err) {
      const motivo = err instanceof ResolucaoDaOcorrenciaError ? err.motivo : 'indisponivel';
      setErro(t.erros[motivo]);
    } finally {
      setRegistrando(false);
    }
  }

  return (
    <fieldset className="mt-sc-3 space-y-sc-3" data-testid={`ocorrencia-resolver-${ocorrenciaId}`}>
      <legend className="text-caption text-fg-dim">{t.escolha}</legend>
      <div className="flex flex-col gap-sc-2">
        {ACOES_DA_RESOLUCAO.map((opcao) => (
          <Radio
            key={opcao}
            name={`resolucao-${ocorrenciaId}`}
            value={opcao}
            checked={acao === opcao}
            disabled={registrando}
            data-testid={`ocorrencia-acao-${opcao}`}
            onChange={() => setAcao(opcao)}
          >
            <span>
              <span className="font-semibold">{t.acao[opcao]}</span>
              <span className="block text-caption text-fg-muted">{t.acaoExplica[opcao]}</span>
            </span>
          </Radio>
        ))}
      </div>
      <Field label={t.nota}>
        <Textarea
          value={nota}
          maxLength={NOTA_MAX}
          disabled={registrando}
          data-testid="ocorrencia-nota"
          onChange={(evento) => setNota(evento.target.value)}
        />
      </Field>
      <div className="flex flex-wrap items-center gap-sc-3">
        <Button
          type="button"
          data-testid="ocorrencia-confirmar"
          disabled={acao === null || registrando}
          onClick={() => void registrar()}
        >
          {registrando ? t.registrando : t.confirmar}
        </Button>
        <Button type="button" variant="ghost" disabled={registrando} onClick={onCancelar}>
          {t.cancelar}
        </Button>
      </div>
      {erro !== null ? (
        <p role="alert" className="text-body-sm text-danger" data-testid="ocorrencia-erro">
          {erro}
        </p>
      ) : null}
    </fieldset>
  );
}
